import { useState, useEffect } from 'react';
import { doc, onSnapshot, query, collection, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { FIREBASE_CONFIG } from '../config/constants';

export interface SubscriptionFeatures {
  customScenarios: boolean;
  analytics: boolean;
  prioritySupport: boolean;
}

const PREMIUM_FEATURES: SubscriptionFeatures = {
  customScenarios: true,
  analytics: true,
  prioritySupport: true
};

const DEFAULT_FEATURES: SubscriptionFeatures = {
  customScenarios: false,
  analytics: false,
  prioritySupport: false
};

export const useSubscription = (uid: string | null) => {
  const [subscriptionStatus, setSubscriptionStatus] = useState<'premium' | null>(null);
  const [interviewsRemaining, setInterviewsRemaining] = useState<number>(0);
  const [hasPurchasedPremium, setHasPurchasedPremium] = useState<boolean>(false);
  const [totalInterviewsCompleted, setTotalInterviewsCompleted] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!uid) {
      setSubscriptionStatus(null);
      setInterviewsRemaining(0);
      setHasPurchasedPremium(false);
      setLoading(false);
      return;
    }

    const userRef = doc(db, 'users', uid);
    const unsubscribe = onSnapshot(userRef, 
      async (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          setSubscriptionStatus(data.subscriptionStatus || null);
          
          // Get accurate count of completed interviews
          try {
            const interviewsQuery = query(
              collection(db, FIREBASE_CONFIG.INTERVIEW_COLLECTION()),
              where('uid', '==', uid),
              where('has_feedback', '==', true)
            );
            
            const querySnapshot = await getDocs(interviewsQuery);
            const completedCount = querySnapshot.size;
            const remainingCount = Math.max(0, (data.interview_quota || 0) - completedCount);
            
            setInterviewsRemaining(remainingCount);
          } catch (error) {
            console.error('Error counting interviews:', error);
            setInterviewsRemaining(0);
          }
          
          setHasPurchasedPremium(data.has_purchased_premium || false);
          setTotalInterviewsCompleted(data.total_interviews_completed || 0);
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching subscription:', error);
        setError(error.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [uid]);

  const canStartInterview = (): boolean => {
    return interviewsRemaining > 0;
  };

  const canPurchaseTopUp = (): boolean => {
    return hasPurchasedPremium;
  };

  const getFeatures = (): SubscriptionFeatures => {
    return subscriptionStatus === 'premium' ? PREMIUM_FEATURES : DEFAULT_FEATURES;
  };

  const canAccessFeature = (feature: keyof SubscriptionFeatures): boolean => {
    const features = getFeatures();
    return Boolean(features[feature]);
  };

  return {
    subscriptionStatus,
    interviewsRemaining,
    hasPurchasedPremium,
    totalInterviewsCompleted,
    loading,
    error,
    canStartInterview,
    canPurchaseTopUp,
    canAccessFeature,
    getFeatures
  };
}; 